import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Keep the mobile carousel viewport sized to the active slide so media of
    // different aspect ratios don't leave a blank gap above/below shorter slides.
    this.addEventListener('slideshow:select', this.#fitSlideHeight, { signal });
    this.#mobile.addEventListener('change', this.#fitSlideHeight, { signal });
    // The slideshow element can be in the DOM but not yet upgraded, in which
    // case it has no `refs` at all. Reaching through it threw and aborted the
    // rest of this callback; wait for its definition and retry instead.
    if (!this.#observeSlideshow()) {
      customElements.whenDefined('slideshow-component').then(() => {
        if (signal.aborted) return;
        this.#observeSlideshow();
        this.#fitSlideHeight();
      });
    }
    requestAnimationFrame(this.#fitSlideHeight);
  }

  /**
   * Observes the slideshow's container, if it is upgraded and present.
   * @returns {boolean} Whether the container was found.
   */
  #observeSlideshow() {
    const slideshowContainer = this.refs.slideshow?.refs?.slideshowContainer;
    if (!slideshowContainer) return false;

    this.#resizeObserver.observe(slideshowContainer);
    return true;
  }

  #controller = new AbortController();

  #mobile = matchMedia('(max-width: 749px)');

  /** Refit when the carousel width changes (resize, orientation), since each slide's height is width-driven. */
  #resizeObserver = new ResizeObserver((entries) => {
    const width = Math.round(entries[0]?.contentRect.width ?? 0);
    if (width === this.#lastWidth) return;
    this.#lastWidth = width;
    this.#fitSlideHeight();
  });

  #lastWidth = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#resizeObserver.disconnect();
  }

  /**
   * Sizes the carousel viewport (slideshow-slides) to the active slide's height.
   * On desktop the slideshow is hidden in favor of a static grid, so the inline
   * height is cleared and the element sizes itself.
   */
  #fitSlideHeight = () => {
    const slideshow = this.refs.slideshow;
    const scroller = slideshow?.refs?.scroller;
    if (!scroller) return;

    if (!this.#mobile.matches) {
      scroller.style.removeProperty('height');
      return;
    }

    const slides = slideshow.slides;
    const active =
      slides?.[slideshow.current] ?? scroller.querySelector('slideshow-slide[aria-hidden="false"]');
    if (!active) return;

    scroller.style.height = `${Math.round(active.getBoundingClientRect().height)}px`;
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
